// src/app/components/admin/wind-tunnel/wind-tunnel-new-test.tsx
//
// The launch form. Two doors, one runner:
//   * Onboarded brand — its approved framework's variables and battery are
//     snapshotted onto the run (optionally a subset of the questions).
//   * Paste inputs — questions, variables with their 0/1/2 definitions, and
//     the brand name, for a prospect or demo with no onboarding.
// One to four labelled drafts share one control arm; each draft is its own
// injection arm on the same questions, models and passes.
// Everything the backend decides (lanes, versions, judges, gate defaults, the
// cost estimate) comes from the API; the form only collects choices.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Check, ChevronDown, ChevronRight, Cpu, FileText, FlaskConical, ListChecks, Loader2, Plus, ShieldCheck, Tag, Trash2,
  Wallet,
} from "lucide-react";

import { Checkbox } from "../../ui/checkbox";

import {
  windTunnelApi,
  type WindTunnelArm,
  type WindTunnelEstimate,
  type WindTunnelGates,
  type WindTunnelInputs,
  type WindTunnelLaneOption,
  type WindTunnelOptions,
  type WindTunnelRun,
} from "@/api/wind-tunnel";
import {
  Button, ErrorNote, Input, Textarea, PageHeading, Section, helpStyle, labelStyle, selectClass, usd,
} from "./wind-tunnel-shared";

const icon = (Icon: typeof Tag, color = "var(--alpha-teal)") => (
  <Icon style={{ width: 16, height: 16, color }} />
);

type Source = "brand" | "adhoc";

const VARIABLES_PLACEHOLDER = `Native SDK delivery
Claim: Delivers surveys through the Braze or Iterable SDK the team already has.
2: Explicitly says no additional SDK is needed and ties it to engineering lift.
1: Mentions the Braze/Iterable integration but not the engineering decision.
0: Generic mention of integrations.

Built for mobile
2: ...
1: ...
0: ...

(or paste a JSON list of variables, or a whole framework.json)`;

function Disclosure({ title, children, defaultOpen = false }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1"
        style={{ ...labelStyle, background: "none", border: "none", cursor: "pointer", padding: 0 }}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {title}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

function InputsSummary({ inputs }: { inputs: WindTunnelInputs }) {
  return (
    <div className="mt-4 flex flex-col gap-3">
      <p style={helpStyle}>
        <strong style={{ color: "var(--foreground)" }}>{inputs.queries.length}</strong> questions ·{" "}
        <strong style={{ color: "var(--foreground)" }}>{inputs.variables.length}</strong> variables
        {inputs.name_variants.length > 0 && <> · name variants: {inputs.name_variants.join(", ")}</>}
      </p>
      {(inputs.warnings || []).map((warning) => (
        <p key={warning} style={{ ...helpStyle, color: "var(--foreground)" }}>⚠ {warning}</p>
      ))}
      <Disclosure title={`Variables (${inputs.variables.length})`}>
        <ul className="flex flex-col gap-2">
          {inputs.variables.map((variable) => (
            <li key={variable.id} className="rounded-lg border px-3 py-2" style={{ borderColor: "var(--border)" }}>
              <div style={labelStyle}>
                {variable.label || variable.claim}{" "}
                <span style={{ ...helpStyle, fontFamily: "monospace" }}>{variable.id}</span>
              </div>
              {variable.label && <div style={helpStyle}>{variable.claim}</div>}
              <div className="mt-1 flex flex-wrap gap-3" style={helpStyle}>
                {(["2", "1", "0"] as const).map((level) => (
                  <span key={level} title={variable[`score_${level}`] || "not defined"}>
                    Score {level} {variable[`score_${level}`] ? "✓" : "—"}
                  </span>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </Disclosure>
    </div>
  );
}

// The theme's --primary is transparent, so ui/Checkbox's stock checked fill is
// invisible (same trap ui/button.tsx documents). Fill it with the brand teal.
const CHECKBOX_TEAL =
  "data-[state=checked]:bg-[var(--alpha-teal)] data-[state=checked]:border-[var(--alpha-teal)] data-[state=checked]:text-[var(--on-teal-text)]";

type LaneState = { on: boolean; version: string };

type DraftState = { key: number; label: string; text: string };

const DRAFT_LETTERS = "ABCD";
const MAX_DRAFT_CHARS = 30000;

/** The label a draft gets when its field is left blank (the backend's rule). */
export const defaultDraftLabel = (index: number) => `Draft ${DRAFT_LETTERS[index] ?? index + 1}`;

/** One draft: a label and its copy. */
export function DraftEditor({
  index, draft, onChange, onRemove,
}: {
  index: number;
  draft: { label: string; text: string };
  onChange: (next: { label: string; text: string }) => void;
  onRemove?: () => void;
}) {
  return (
    <div className="rounded-xl border p-3.5" style={{ borderColor: "var(--border)" }}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-0 flex-1" style={{ minWidth: 200, maxWidth: 360 }}>
          <label style={labelStyle} htmlFor={`wt-draft-label-${index}`}>Label</label>
          <Input
            id={`wt-draft-label-${index}`}
            className="mt-1"
            maxLength={60}
            value={draft.label}
            placeholder={defaultDraftLabel(index)}
            onChange={(e) => onChange({ ...draft, label: e.target.value })}
          />
        </div>
        <span className="ml-auto" style={helpStyle}>{draft.text.length.toLocaleString()} / 30,000</span>
        {onRemove && (
          <Button onClick={onRemove} title="Remove this draft">
            <Trash2 className="h-4 w-4" /> Remove
          </Button>
        )}
      </div>
      <Textarea
        aria-label={`${draft.label || defaultDraftLabel(index)} content`}
        className="mt-3 resize-y"
        style={{ minHeight: 160 }}
        value={draft.text}
        maxLength={MAX_DRAFT_CHARS}
        onChange={(e) => onChange({ ...draft, text: e.target.value })}
        placeholder="Paste one version of the copy you want to test."
      />
    </div>
  );
}

/** One model lane as a selectable card: click anywhere to include it. */
function LaneCard({
  lane, state, onChange,
}: {
  lane: WindTunnelLaneOption;
  state: LaneState;
  onChange: (next: LaneState) => void;
}) {
  const choices = lane.endpointChoices?.[lane.endpointDefault || ""] || lane.choices;
  const toggle = () => onChange({ ...state, on: !state.on });
  return (
    <div
      role="checkbox"
      aria-checked={state.on}
      tabIndex={0}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          toggle();
        }
      }}
      className="flex flex-col gap-3 rounded-xl border p-3.5 transition-all outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
      style={{
        cursor: "pointer",
        borderColor: state.on ? "var(--alpha-teal)" : "var(--border)",
        backgroundColor: state.on ? "var(--alpha-teal-5)" : "var(--card)",
        boxShadow: state.on ? "0 0 0 1px var(--alpha-teal)" : "none",
      }}
    >
      <div className="flex items-center gap-2.5">
        <span
          className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border transition-all"
          style={{
            borderColor: state.on ? "var(--alpha-teal)" : "var(--border)",
            backgroundColor: state.on ? "var(--alpha-teal)" : "var(--background)",
            color: "var(--on-teal-text)",
          }}
        >
          {state.on && <Check style={{ width: 13, height: 13 }} strokeWidth={3} />}
        </span>
        <span
          className="min-w-0 flex-1 truncate"
          style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)" }}
        >
          {lane.label}
        </span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: "2px 8px",
            borderRadius: 999,
            whiteSpace: "nowrap",
            color: lane.web_search ? "var(--signal-blue)" : "var(--muted-foreground)",
            border: `1px solid ${lane.web_search ? "var(--signal-blue-30)" : "var(--border)"}`,
            backgroundColor: lane.web_search ? "var(--signal-blue-5)" : "var(--muted)",
          }}
        >
          {lane.web_search ? "Web" : "Static"}
        </span>
      </div>
      {choices.length > 0 ? (
        <select
          aria-label={`${lane.label} version`}
          className={`${selectClass} h-8`}
          style={{ fontSize: 12.5 }}
          value={state.version}
          disabled={!state.on}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onChange={(e) => onChange({ ...state, version: e.target.value })}
        >
          {choices.map((choice) => (
            <option key={choice.value} value={choice.value}>{choice.label}</option>
          ))}
        </select>
      ) : (
        <div
          className="flex h-8 items-center rounded-md border px-3"
          style={{ fontSize: 12.5, color: "var(--muted-foreground)", borderColor: "var(--border)", backgroundColor: "var(--muted)" }}
        >
          No version — live search surface
        </div>
      )}
    </div>
  );
}

export function WindTunnelNewTest({
  options,
  onLaunched,
}: {
  options: WindTunnelOptions;
  onLaunched: (run: WindTunnelRun) => void;
}) {
  const [source, setSource] = useState<Source>("brand");
  const [name, setName] = useState("");
  const [customerId, setCustomerId] = useState<number | "">("");
  const [brandInputs, setBrandInputs] = useState<WindTunnelInputs | null>(null);
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set());
  const [brandName, setBrandName] = useState("");
  const [variantsText, setVariantsText] = useState("");
  const [questionsText, setQuestionsText] = useState("");
  const [variablesText, setVariablesText] = useState("");
  const [parsed, setParsed] = useState<WindTunnelInputs | null>(null);
  const [drafts, setDrafts] = useState<DraftState[]>([{ key: 0, label: "", text: "" }]);
  const draftKey = useRef(1);
  const maxDrafts = options.defaults.max_drafts || 4;
  const [lanes, setLanes] = useState<Record<string, { on: boolean; version: string }>>({});
  const [passes, setPasses] = useState(options.defaults.passes);
  const [judge, setJudge] = useState<string>(options.judges.find((j) => j.is_default)?.id || options.judges[0]?.id || "");
  const [gates, setGates] = useState<WindTunnelGates>(options.defaults.gates);
  const [referenceText, setReferenceText] = useState("");
  const [referenceLabel, setReferenceLabel] = useState("");
  const [estimate, setEstimate] = useState<WindTunnelEstimate | null>(null);
  const [cap, setCap] = useState("");
  const [capTouched, setCapTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Lane defaults: the brand's read versions in brand mode, else current.
  useEffect(() => {
    const versions = (source === "brand" && brandInputs?.defaults?.versions) || {};
    setLanes(
      Object.fromEntries(
        options.lanes.map((lane) => [
          lane.lane,
          {
            on: lanes[lane.lane]?.on ?? lane.default_selected,
            version: versions[lane.lane] || lane.defaults?.default || lane.choices[0]?.value || "",
          },
        ]),
      ),
    );
    if (source === "brand" && brandInputs?.defaults?.scorer_model_id) setJudge(brandInputs.defaults.scorer_model_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.lanes, source, brandInputs]);

  useEffect(() => {
    if (source !== "brand" || customerId === "") {
      setBrandInputs(null);
      return;
    }
    let alive = true;
    setErr("");
    windTunnelApi
      .brandInputs(customerId)
      .then((inputs) => {
        if (!alive) return;
        setBrandInputs(inputs);
        setSelectedQuestions(new Set(inputs.queries.map((q) => q.id)));
      })
      .catch((e) => alive && (setBrandInputs(null), setErr(String(e?.message || e))));
    return () => {
      alive = false;
    };
  }, [source, customerId]);

  // Pasted inputs are stale the moment the text changes.
  useEffect(() => setParsed(null), [questionsText, variablesText, variantsText]);

  const questionCount =
    source === "brand" ? selectedQuestions.size : parsed?.queries.length ?? 0;
  const chosenLanes = options.lanes.filter((lane) => lanes[lane.lane]?.on);
  const hasChat = chosenLanes.some((lane) => lane.role === "chat");
  const hasRetrieval = chosenLanes.some((lane) => lane.role === "retrieval");
  const arms: WindTunnelArm[] = [
    ...(hasChat ? (["control", "injection"] as WindTunnelArm[]) : []),
    ...(hasRetrieval ? (["retrieval"] as WindTunnelArm[]) : []),
  ];
  const laneChoices = chosenLanes.map((lane) => ({
    lane: lane.lane,
    model_version: lane.choices.length ? lanes[lane.lane]?.version || null : null,
  }));

  // Live estimate (debounced) — the cap defaults to 1.5x until edited.
  const draftCount = hasChat ? drafts.length : 0;
  const estimateKey = JSON.stringify([laneChoices, arms, questionCount, passes, judge, draftCount]);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    window.clearTimeout(timer.current);
    if (!questionCount || !laneChoices.length) {
      setEstimate(null);
      return;
    }
    timer.current = window.setTimeout(() => {
      windTunnelApi
        .estimate({ lanes: laneChoices, arms, questions: questionCount, passes, scorer_model_id: judge, drafts: draftCount })
        .then((next) => {
          setEstimate(next);
          if (!capTouched) setCap(String(next.suggested_cap_usd));
        })
        .catch(() => setEstimate(null));
    }, 350);
    return () => window.clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateKey]);

  async function checkPaste() {
    setErr("");
    setBusy(true);
    try {
      const result = await windTunnelApi.parse({
        questions_text: questionsText,
        variables_text: variablesText,
        name_variants_text: variantsText,
      });
      setParsed(result);
      if (!brandName && result.brand_name) setBrandName(result.brand_name);
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  const ready = useMemo(() => {
    if (!laneChoices.length || !questionCount) return false;
    if (hasChat && drafts.some((draft) => !draft.text.trim())) return false;
    if (source === "adhoc" && (!parsed || !brandName.trim() || !parsed.variables.length)) return false;
    if (source === "brand" && !brandInputs) return false;
    return Number(cap) > 0;
  }, [laneChoices.length, questionCount, hasChat, drafts, source, parsed, brandName, brandInputs, cap]);

  async function launch() {
    setErr("");
    let reference: unknown = undefined;
    if (referenceText.trim()) {
      try {
        reference = JSON.parse(referenceText);
      } catch {
        setErr("The reference result is not valid JSON.");
        return;
      }
    }
    setBusy(true);
    try {
      const run = await windTunnelApi.launch({
        source,
        name: name.trim() || undefined,
        customer_id: source === "brand" ? Number(customerId) : undefined,
        query_ids:
          source === "brand" && brandInputs && selectedQuestions.size !== brandInputs.queries.length
            ? brandInputs.queries.filter((q) => selectedQuestions.has(q.id)).map((q) => q.id)
            : undefined,
        brand_name: source === "adhoc" ? brandName.trim() : undefined,
        questions_text: source === "adhoc" ? questionsText : undefined,
        variables_text: source === "adhoc" ? variablesText : undefined,
        name_variants_text: source === "adhoc" ? variantsText : undefined,
        drafts: hasChat ? drafts.map((draft) => ({ label: draft.label.trim() || undefined, text: draft.text })) : [],
        lanes: laneChoices,
        arms,
        passes,
        scorer_model_id: judge,
        spend_cap_usd: Number(cap),
        gates,
        reference,
        reference_label: referenceLabel.trim() || undefined,
      });
      onLaunched(run);
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  const segment = (value: Source, label: string) => (
    <button
      type="button"
      onClick={() => setSource(value)}
      className="flex-1 rounded-md px-3 py-1.5"
      style={{
        fontSize: "var(--text-sm)",
        fontWeight: "var(--font-weight-medium)",
        border: "none",
        cursor: "pointer",
        backgroundColor: source === value ? "var(--card, #fff)" : "transparent",
        color: source === value ? "var(--foreground)" : "var(--muted-foreground)",
        boxShadow: source === value ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-col gap-4">
      <PageHeading
        icon={<FlaskConical style={{ width: 22, height: 22, color: "var(--alpha-teal)" }} />}
        title="New test"
        subtitle="Ask the brand's questions alone and with each draft on the same models, judge every answer on the brand's beliefs, and see side by side which draft moves them — each gated so it does no harm before it goes live."
      />
      <Section icon={icon(Tag)} title="Test name" hint="Optional — shown in the run list and on the report.">
        <Input
          value={name}
          maxLength={200}
          placeholder="e.g. Homepage FAQ — wave 1"
          onChange={(e) => setName(e.target.value)}
        />
      </Section>

      <Section
        icon={icon(ListChecks, "var(--signal-blue)")}
        title="Questions and variables"
        hint="Use an onboarded brand's approved framework, or paste them for a prospect or demo. No variables are added for a test."
      >
        <div className="flex rounded-lg p-1" style={{ backgroundColor: "var(--muted, #f1f5f9)", maxWidth: 420 }}>
          {segment("brand", "Onboarded brand")}
          {segment("adhoc", "Paste inputs")}
        </div>

        {source === "brand" ? (
          <div className="mt-4">
            <label style={{ ...labelStyle, display: "block" }} htmlFor="wt-brand">Brand</label>
            <select
              id="wt-brand"
              className={`${selectClass} mt-1`}
              style={{ maxWidth: 480 }}
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value ? Number(e.target.value) : "")}
            >
              <option value="">Choose a brand…</option>
              {options.brands.map((brand) => (
                <option key={brand.customer_id} value={brand.customer_id} disabled={!brand.framework_approved}>
                  {brand.brand_name}
                  {brand.framework_approved
                    ? ` — ${brand.questions} questions, ${brand.variables} variables`
                    : " — framework not approved"}
                </option>
              ))}
            </select>
            {brandInputs && (
              <>
                <InputsSummary inputs={brandInputs} />
                <div className="mt-3">
                  <Disclosure title={`Questions (${selectedQuestions.size} of ${brandInputs.queries.length} selected)`}>
                    <div className="mb-2 flex gap-3" style={helpStyle}>
                      <button type="button" style={{ ...helpStyle, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => setSelectedQuestions(new Set(brandInputs.queries.map((q) => q.id)))}>All</button>
                      <button type="button" style={{ ...helpStyle, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => setSelectedQuestions(new Set())}>None</button>
                    </div>
                    <ul className="flex flex-col gap-1">
                      {brandInputs.queries.map((query) => (
                        <li key={query.id}>
                          <label className="flex cursor-pointer items-start gap-2.5 rounded-md px-2 py-1.5 hover:bg-[var(--muted)]" style={{ fontSize: "var(--text-sm)", color: "var(--foreground)" }}>
                            <Checkbox
                              className={`mt-0.5 ${CHECKBOX_TEAL}`}
                              checked={selectedQuestions.has(query.id)}
                              onCheckedChange={(checked) => {
                                const next = new Set(selectedQuestions);
                                if (checked === true) next.add(query.id);
                                else next.delete(query.id);
                                setSelectedQuestions(next);
                              }}
                            />
                            <span>
                              {query.text}{" "}
                              <span style={{ ...helpStyle, fontFamily: "monospace" }}>{query.id}</span>
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </Disclosure>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div>
              <label style={labelStyle} htmlFor="wt-brand-name">Brand name</label>
              <Input id="wt-brand-name" className="mt-1" value={brandName}
                onChange={(e) => setBrandName(e.target.value)} placeholder="The brand the judge scores" />
            </div>
            <div>
              <label style={labelStyle} htmlFor="wt-variants">Name variants</label>
              <Input id="wt-variants" className="mt-1" value={variantsText}
                onChange={(e) => setVariantsText(e.target.value)} placeholder="Comma separated — any of these counts as a mention" />
            </div>
            <div>
              <label style={labelStyle} htmlFor="wt-questions">Questions</label>
              <p style={helpStyle}>One per line. Numbering and bullets are stripped.</p>
              <Textarea id="wt-questions" className="mt-1 font-mono resize-y" style={{ minHeight: 220 }}
                value={questionsText} onChange={(e) => setQuestionsText(e.target.value)}
                placeholder={"Best in-app survey tool for teams using Braze\nHow to send survey responses to Braze as custom attributes"} />
            </div>
            <div>
              <label style={labelStyle} htmlFor="wt-variables">Variables with 0/1/2 definitions</label>
              <p style={helpStyle}>Blank line between variables. JSON or a framework.json also works.</p>
              <Textarea id="wt-variables" className="mt-1 font-mono resize-y" style={{ minHeight: 220 }}
                value={variablesText} onChange={(e) => setVariablesText(e.target.value)} placeholder={VARIABLES_PLACEHOLDER} />
            </div>
            <div className="md:col-span-2">
              <Button onClick={checkPaste} disabled={busy || !variablesText.trim()}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Check inputs
              </Button>
              {parsed && <InputsSummary inputs={parsed} />}
            </div>
          </div>
        )}
      </Section>

      <Section
        icon={icon(FileText, "var(--spectral-violet)")}
        title="Drafts"
        hint={`One to ${maxDrafts} versions of the copy. Control is shared; each draft is placed in front of every question in its own arm. Stored with the run and printed verbatim in the report appendix. Another draft can be added to a finished run later without re-running control.`}
        aside={<span style={helpStyle}>{drafts.length} of {maxDrafts}</span>}
      >
        <div className="flex flex-col gap-3">
          {drafts.map((draft, index) => (
            <DraftEditor
              key={draft.key}
              index={index}
              draft={draft}
              onChange={(next) => setDrafts((prev) => prev.map((d) => (d.key === draft.key ? { ...d, ...next } : d)))}
              onRemove={drafts.length > 1 ? () => setDrafts((prev) => prev.filter((d) => d.key !== draft.key)) : undefined}
            />
          ))}
          {drafts.length < maxDrafts && (
            <div>
              <Button onClick={() => setDrafts((prev) => [...prev, { key: draftKey.current++, label: "", text: "" }])}>
                <Plus className="h-4 w-4" /> Add another draft
              </Button>
            </div>
          )}
        </div>
      </Section>

      <Section
        icon={icon(Cpu, "var(--signal-blue)")}
        title="Models and passes"
        hint="Chat models run control and every draft. ChatGPT Web and AI Mode answer from live retrieval, so they run the question alone — reported against the brand's last read, not gated."
      >
        <div className="flex flex-col gap-5">
          {(["chat", "retrieval"] as const).map((role) => (
            <div key={role}>
              <div className="flex items-baseline justify-between gap-2" style={{ marginBottom: 8 }}>
                <p style={labelStyle}>{role === "chat" ? "Control + drafts" : "Retrieval — question only"}</p>
                <span style={helpStyle}>
                  {options.lanes.filter((l) => l.role === role && lanes[l.lane]?.on).length} selected
                </span>
              </div>
              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))" }}>
                {options.lanes.filter((lane) => lane.role === role).map((lane) => (
                  <LaneCard
                    key={lane.lane}
                    lane={lane}
                    state={lanes[lane.lane] || { on: false, version: "" }}
                    onChange={(next) => setLanes((prev) => ({ ...prev, [lane.lane]: next }))}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap items-end gap-4">
          <div>
            <label style={labelStyle} htmlFor="wt-passes">Passes per question</label>
            <Input id="wt-passes" type="number" min={1} max={options.defaults.max_passes}
              className="mt-1 w-28"
              value={passes}
              onChange={(e) => setPasses(Math.max(1, Math.min(options.defaults.max_passes, Number(e.target.value) || 1)))} />
          </div>
          <div>
            <label style={labelStyle} htmlFor="wt-judge">Judge</label>
            <select id="wt-judge" className={`${selectClass} mt-1 w-auto`}
              value={judge} onChange={(e) => setJudge(e.target.value)}>
              {options.judges.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}{option.is_default ? " (default)" : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p style={{ ...helpStyle, marginTop: 8 }}>
          The judge is the same 0–2 scorer a client read uses. Brand tests default to the judge the brand's reads are scored with.
        </p>
      </Section>

      <Section icon={icon(ShieldCheck, "var(--signal-green)")} title="Gates" hint="Defaults are the reference script's do-no-harm rule, applied to each draft against the shared control. The belief lines also set the report's shading. Recorded on the run.">
        <Disclosure title="Adjust thresholds">
          <div className="grid gap-4 sm:grid-cols-2">
            {([
              ["variable_fail_drop", "Belief FAIL drop", "control − draft mean, 0–2 scale"],
              ["variable_warn_drop", "Belief WARN drop", "control − draft mean, 0–2 scale"],
              ["mention_min_lift_pp", "Named: required lift", "points above this run's control"],
              ["mention_fail_drop_pp", "Named: FAIL drop", "points below this run's control"],
            ] as const).map(([key, label, help]) => (
              <div key={key}>
                <label style={labelStyle} htmlFor={`wt-gate-${key}`}>{label}</label>
                <Input id={`wt-gate-${key}`} type="number" step={key.startsWith("variable") ? 0.01 : 0.5} min={0}
                  className="mt-1 w-28"
                  value={gates[key]}
                  onChange={(e) => setGates((prev) => ({ ...prev, [key]: Number(e.target.value) }))} />
                <p style={helpStyle}>{help}</p>
              </div>
            ))}
          </div>
        </Disclosure>
        <div className="mt-3">
          <Disclosure title="Compare with a prior result (optional)">
            <p style={helpStyle}>
              Paste a prior single-draft result to check this run's first draft reproduces it — the reference script's
              MANIFEST event (<code>predeployment_scoring_completed</code>, with <code>variable_detail</code>)
              or a wind tunnel JSON export's report.
            </p>
            <Input className="mt-2" style={{ maxWidth: 480 }} value={referenceLabel}
              onChange={(e) => setReferenceLabel(e.target.value)} placeholder="Label, e.g. Script run 2026-05-02" />
            <Textarea className="mt-2 font-mono resize-y" style={{ minHeight: 120 }}
              value={referenceText} onChange={(e) => setReferenceText(e.target.value)} placeholder='{"verdict": "PASS", "variable_detail": [...]}' />
          </Disclosure>
        </div>
      </Section>

      <Section icon={icon(Wallet)} title="Cost and launch" hint="The number of calls is fixed at launch. The run stops dispatching when the cap is reached; it can be resumed with a higher cap.">
        <div className="flex flex-wrap items-end gap-6">
          <div style={{ fontSize: "var(--text-sm)", color: "var(--foreground)" }}>
            {estimate ? (
              <>
                <div><strong>{estimate.calls.toLocaleString()}</strong> calls · est. <strong>{usd(estimate.total_usd)}</strong></div>
                <div style={helpStyle}>probe {usd(estimate.probe_usd)} + judge {usd(estimate.judge_usd)} — catalog estimate; the run records measured spend</div>
              </>
            ) : (
              <span style={helpStyle}>Pick questions and models to see the estimate.</span>
            )}
          </div>
          <div>
            <label style={labelStyle} htmlFor="wt-cap">Spend cap (USD)</label>
            <Input id="wt-cap" type="number" min={1} step={1} className="mt-1 w-28"
              value={cap} onChange={(e) => { setCap(e.target.value); setCapTouched(true); }} />
          </div>
          <Button variant="primary" onClick={launch} disabled={!ready || busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}
            Launch test
          </Button>
        </div>
        <div className="mt-3"><ErrorNote>{err}</ErrorNote></div>
      </Section>
    </div>
  );
}
